export type AgentId =
  | 'claude-code'
  | 'cursor'
  | 'windsurf'
  | 'opencode'
  | 'codex'

export type SetupStep = {
  title: string
  description: string
  code?: string
  language?: string
  note?: string
  link?: string
  isApiKeyStep?: boolean
}

export type Agent = {
  id: AgentId
  name: string
  description: string
  steps: SetupStep[]
}

export const AGENTS: Agent[] = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    description: "Anthropic's AI coding agent with MCP support",
    steps: [
      {
        title: 'Create your Sevalla API key',
        description:
          'Create an API key to authenticate Claude Code with your Sevalla account.',
        isApiKeyStep: true,
        note: 'Keep your API key secure. Never share it or commit it to version control.',
      },
      {
        title: 'Add the Sevalla MCP server',
        description:
          'Add the Sevalla MCP server to your project config `.mcp.json` (or globally in `~/.claude.json`):',
        code: `{
  "mcpServers": {
    "sevalla": {
      "type": "http",
      "url": "https://mcp.sevalla.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
        language: 'json',
        note: 'Replace YOUR_API_KEY with the key you created in step 1. You can verify the connection with `claude mcp list`.',
      },
      {
        title: 'Start using Sevalla in Claude Code',
        description:
          'Open Claude Code and start a conversation. You can now manage your Sevalla services directly from Claude Code. Try: "List my Sevalla apps" or "Deploy a new application".',
      },
    ],
  },
  {
    id: 'cursor',
    name: 'Cursor',
    description: 'AI-powered code editor with MCP support',
    steps: [
      {
        title: 'Create your Sevalla API key',
        description:
          'Create an API key to authenticate Cursor with your Sevalla account.',
        isApiKeyStep: true,
        note: 'Keep your API key secure. Never share it or commit it to version control.',
      },
      {
        title: 'Configure the MCP server',
        description:
          'Open Cursor Settings → Features → MCP. Add a new MCP server, or create/edit the file `.cursor/mcp.json` in your project (or `~/.cursor/mcp.json` globally):',
        code: `{
  "mcpServers": {
    "sevalla": {
      "url": "https://mcp.sevalla.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
        language: 'json',
        note: 'Replace YOUR_API_KEY with the key you created in step 1.',
      },
      {
        title: 'Start using Sevalla in Cursor',
        description:
          'Open the Cursor AI chat panel (Cmd/Ctrl+L) and enable Agent mode. You can now manage your Sevalla services. Try: "Show my Sevalla apps" or "Create a new database".',
      },
    ],
  },
  {
    id: 'windsurf',
    name: 'Windsurf',
    description: 'AI-native IDE by Codeium with Cascade agent',
    steps: [
      {
        title: 'Create your Sevalla API key',
        description:
          'Create an API key to authenticate Windsurf with your Sevalla account.',
        isApiKeyStep: true,
        note: 'Keep your API key secure. Never share it or commit it to version control.',
      },
      {
        title: 'Configure the MCP server',
        description:
          'Open Windsurf Settings → Cascade → MCP Servers, or edit `~/.codeium/windsurf/mcp_config.json`:',
        code: `{
  "mcpServers": {
    "sevalla": {
      "serverUrl": "https://mcp.sevalla.com/mcp",
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
        language: 'json',
        note: 'Replace YOUR_API_KEY with the key you created in step 1.',
      },
      {
        title: 'Start using Sevalla in Windsurf',
        description:
          'Open the Cascade panel in Windsurf and start a conversation. Try: "List my Sevalla services" or "Deploy a new app to Sevalla".',
      },
    ],
  },
  {
    id: 'opencode',
    name: 'OpenCode',
    description: 'Open-source terminal AI coding agent',
    steps: [
      {
        title: 'Create your Sevalla API key',
        description:
          'Create an API key to authenticate OpenCode with your Sevalla account.',
        isApiKeyStep: true,
        note: 'Keep your API key secure. Never share it or commit it to version control.',
      },
      {
        title: 'Configure the MCP server',
        description:
          'Add the Sevalla MCP server to `opencode.json` in your project root (or `~/.config/opencode/opencode.json` for global):',
        code: `{
  "$schema": "https://opencode.ai/config.json",
  "mcp": {
    "sevalla": {
      "type": "remote",
      "url": "https://mcp.sevalla.com/mcp",
      "oauth": false,
      "headers": {
        "Authorization": "Bearer YOUR_API_KEY"
      }
    }
  }
}`,
        language: 'json',
        note: 'Replace YOUR_API_KEY with the key you created in step 1. You can verify the connection with `opencode mcp list`.',
      },
      {
        title: 'Start using Sevalla in OpenCode',
        description:
          'Run `opencode` in your terminal and start a conversation. Try: "List my Sevalla apps" or "Show my running services".',
      },
    ],
  },
  {
    id: 'codex',
    name: 'Codex',
    description: "OpenAI's CLI coding agent with MCP support",
    steps: [
      {
        title: 'Create your Sevalla API key',
        description:
          'Create an API key to authenticate Codex with your Sevalla account.',
        isApiKeyStep: true,
        note: 'Keep your API key secure. Never share it or commit it to version control.',
      },
      {
        title: 'Configure the MCP server',
        description:
          'Add the Sevalla MCP server to your Codex config file `~/.codex/config.toml`:',
        code: `[mcp_servers.sevalla]
url = "https://mcp.sevalla.com/mcp"
env_http_headers = { Authorization = "SEVALLA_AUTH_HEADER" }`,
        language: 'toml',
        note: 'Then export the key in your shell: `export SEVALLA_AUTH_HEADER="Bearer YOUR_API_KEY"` (using the key you created in step 1). Verify with `codex mcp list`.',
      },
      {
        title: 'Start using Sevalla in Codex',
        description:
          'Run `codex` in your terminal and start a conversation. Try: "Show my Sevalla services" or "Deploy a new application".',
      },
    ],
  },
]

export type PathStepLink = {
  label: string
  url: string
}

export type PathStep = {
  title: string
  description: string
  link?: string
  links?: PathStepLink[]
}

export const DEPLOY_STEPS: PathStep[] = [
  {
    title: 'Create an application',
    description:
      'Deploy your app, database, static site, or object storage from your own code or container image.',
    link: '/apps/create',
  },
  {
    title: 'Configure your application',
    description:
      'Set environment variables, choose a build strategy, configure networking, and define processes from the tabs in your app.',
    links: [
      {
        label: 'Environment variables',
        url: 'https://docs.sevalla.com/applications/environment-variables',
      },
      {
        label: 'Build strategy',
        url: 'https://docs.sevalla.com/applications/build-options',
      },
      {
        label: 'Networking',
        url: 'https://docs.sevalla.com/applications/networking',
      },
      {
        label: 'Processes',
        url: 'https://docs.sevalla.com/applications/processes',
      },
    ],
  },
  {
    title: 'Deploy and connect a domain',
    description:
      'Trigger your first deployment and connect a custom domain when you’re ready to go live.',
    links: [
      {
        label: 'Deploy your application',
        url: 'https://docs.sevalla.com/applications/deployments#deploy-your-application',
      },
      {
        label: 'Add a custom domain',
        url: 'https://docs.sevalla.com/applications/domains#add-domain',
      },
    ],
  },
]

export const DATABASE_STEPS: PathStep[] = [
  {
    title: 'Create a database',
    description:
      'Choose from MySQL, PostgreSQL, MariaDB, Redis, or Valkey and provision it in seconds.',
    link: '/databases/create',
  },
  {
    title: 'Connect from your application',
    description:
      'Use the internal or external connection strings from the Networking tab to wire up your app.',
    link: 'https://docs.sevalla.com/databases/networking',
  },
  {
    title: 'Manage and monitor',
    description:
      'Run queries from the Studio tab, schedule backups, and scale your database when you need to.',
  },
]
